import { useEffect, useState, useRef } from 'react';
import moment from 'moment';
import { Modal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import {
  Button,
  Box,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Table } from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import { useViewCentralizedReportsHistory } from './hooks';
import useFormatMessage from '../../../../hooks/useFormatMessage';
import componentMessages from './messages';
import containerMessages from '../../messages';
import generalMessages from '../../../App/messages';
import useBaseTableProps from '../../../../hooks/useBaseTableProps';

const messages = {
  ...containerMessages,
  ...componentMessages,
  ...generalMessages,
};

const NAME = 'centralized-reports-history';

const ViewCentralizedReportsHistoryModal = ({
  isOpen,
  closeModal,
  intl,
  organizationId,
  report,
}) => {
  const formatMessage = useFormatMessage();
  const [dateHasChanged, setDateHasChanged] = useState(false);
  // Track previous isOpen state to detect when modal opens
  const prevIsOpenRef = useRef(false);
  // Initialize dates for 90-day window - will be reset when modal opens
  const [initialFromDate, setInitialFromDate] = useState(() =>
    moment().subtract(90, 'days'),
  );
  const [initialToDate, setInitialToDate] = useState(() => moment());
  const baseTableProps = useBaseTableProps();
  const {
    name,
    allColumnsHidden,
    columnOptions,
    count,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
    handleSortChange,
    isLoading,
    order,
    orderBy,
    rows,
    filterState,
    dateState,
    handleDateSubmit,
    handleDateReset,
    totalCount,
    refetch,
  } = useViewCentralizedReportsHistory({
    NAME,
    organizationId,
    report,
    fromDate: initialFromDate,
    toDate: initialToDate,
  });

  useEffect(() => {
    if (dateHasChanged) return;
    const { fromDate, toDate } = dateState?.createdAt || {};
    const initialDateHasChanged =
      !moment(fromDate).isSame(initialFromDate) ||
      !moment(toDate).isSame(initialToDate);
    if (initialDateHasChanged) {
      setDateHasChanged(true);
    }
  }, [dateHasChanged, dateState.createdAt, initialFromDate, initialToDate]);

  // Refetch data and reset dates when modal opens to ensure latest reports are shown
  useEffect(() => {
    const modalJustOpened = isOpen && !prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;

    if (modalJustOpened) {
      // Reset dates to fresh 90-day window
      setInitialFromDate(moment().subtract(90, 'days'));
      setInitialToDate(moment());
      if (refetch) {
        refetch();
      }
    }
  }, [isOpen, refetch]);

  const handleClose = () => {
    clearDates();
    closeModal();
  };
  const clearDates = () => {
    Object.keys(dateState).forEach((key) => {
      handleDateReset(key);
    });
    setDateHasChanged(false);
  };
  const actions = (
    <>
      <Button variant="primarySubtle" onClick={handleClose}>
        {formatMessage(messages.close)}
      </Button>
    </>
  );

  return (
    <>
      <Modal
        actions={actions}
        open={isOpen}
        onClose={handleClose}
        title={formatMessage(messages.viewHistory)}
      >
        <Box width={450}>
          <Table
            {...baseTableProps}
            name={name}
            allColumnsHidden={allColumnsHidden}
            columnOptions={columnOptions}
            selectedColumns={selectedColumns}
            handleColumnChange={handleColumnChange}
            filterState={filterState}
            handleSortChange={handleSortChange}
            headers={filteredHeaders}
            intl={intl}
            isLoading={isLoading}
            order={order}
            orderBy={orderBy}
            rows={rows}
            count={count}
            sx={{
              padding: 0,
              overflow: 'hidden',
            }}
            emptyStateMessage={`${
              dateHasChanged
                ? formatMessage(messages.noReportForSelectedDate)
                : formatMessage(messages.noReportForLast90Days)
            }`}
            // Custom height to fix scroll issues when displaying 2-3 items.
            // Floor at two rows so the empty-state overlay, which the library
            // centers over the table area, has body space below the header.
            height={Math.min(120 + Math.max(rows.length, 2) * 52, 450)}
            dateState={dateState}
            handleDateSubmit={handleDateSubmit}
            totalCount={totalCount}
          />
        </Box>
      </Modal>
    </>
  );
};

export default ViewCentralizedReportsHistoryModal;
